import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { ProductFormState } from "../../../services/types/product.type";
import ModalWrap from "../../ui/wrappers/ModalWrap/ModalWrap";
import "./ProductModal.css";
import {
  useCreateProductMutation,
  useGetProductByIdQuery,
  useUpdateProductMutation,
} from "../../../store/products/productsApi";
import LoadBlock from "../LoadBlock/LoadBlock";
import { TextInput } from "../../ui/inputs/TextInput/TextInput";
import { ButtonWrap } from "../../ui/wrappers/ButtonWrap/ButtonWrap";
import { TextButton } from "../../ui/buttons/TextButton/TextButton";
import { TextArea } from "../../ui/inputs/TextArea/TextArea";
import { NumberInput } from "../../ui/inputs/NumberInput/NumberInput";
import { TextError } from "../../ui/errors/TextError/TextError";
import { useGetProductTypesQuery } from "../../../store/productTypes/productTypesApi";
import { useGetCollectionsQuery } from "../../../store/collections/collectionsApi";
import { Select } from "../../ui/inputs/Select/Select";
import { Preloader } from "../../ui/Preloader/Preloader";
import { statusOptions } from "../../../utils/statusOptions";
import {
  useDeleteProductImageMutation,
  useUpdateProductImageMutation,
  useUploadProductImageMutation,
} from "../../../store/productImages/productImagesApi";
import {
  PhotosManager,
  PhotosManagerRef,
} from "../PhotosManager/PhotosManager";
import { ProductImage } from "../../../services/types/productImage.type";

interface ProductModalProps {
  uuidItem?: string;
  closeModal: () => void;
  updatePage: () => void;
}

const defaultState: ProductFormState = {
  name: "",
  description: "",
  composition: "",
  price: "",
  status: "new",
  productTypeUuid: "",
  collectionUuid: "",
  comment: "",
};

export const ProductModal = ({
  uuidItem,
  closeModal,
  updatePage,
}: ProductModalProps) => {
  const [formValues, setFormValues] = useState<ProductFormState>(defaultState);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Создаем ref для PhotosManager (неполный контролируемый компонент)
  const photosManagerRef = useRef<PhotosManagerRef>(null);
  // Ключ для PhotosManager, чтобы заставить его перемонтироваться при изменении товара
  const [photosKey, setPhotosKey] = useState<string>("new");

  // Получаем данные товара, если редактирование (uuidItem задан)
  const { data: fullProduct, isLoading: isProductLoading } =
    useGetProductByIdQuery(
      { uuid: uuidItem || "", fullImages: true },
      { skip: !uuidItem, refetchOnMountOrArgChange: true },
    );
  const { data: productTypes, isLoading: isLoadingPTypes } =
    useGetProductTypesQuery(undefined);
  const { data: collections, isLoading: isLoadingCollections } =
    useGetCollectionsQuery(undefined);

  // Мутации API
  const [createProduct, { isLoading: isCreating, error: createError }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating, error: updateError }] =
    useUpdateProductMutation();
  const [uploadProductImage] = useUploadProductImageMutation();
  const [updateProductImage] = useUpdateProductImageMutation();
  const [deleteProductImage] = useDeleteProductImageMutation();

  // При загрузке данных товара (редактирование), инициализируем форму и фото
  useEffect(() => {
    if (uuidItem && fullProduct) {
      setFormValues({
        name: fullProduct.name,
        description: fullProduct.description,
        composition: fullProduct.composition || "",
        price: fullProduct.price,
        status: fullProduct.status || "available",
        comment: fullProduct.comment || "",
        productTypeUuid: fullProduct.productType.uuid,
        collectionUuid: fullProduct.collection.uuid,
      });
      // PhotosManager получает данные через проп photos; благодаря ключу компонент перезагружается.
      setPhotosKey(`${uuidItem}-${Date.now()}`);
    } else if (!uuidItem) {
      // Инициализация для создания нового товара
      setFormValues({
        name: "",
        description: "",
        composition: "",
        price: "",
        status: "available",
        comment: "",
        productTypeUuid: "",
        collectionUuid: "",
      });
      setPhotosKey("new");
    }
    setFormErrors({});
  }, [uuidItem, fullProduct]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: name === "price" ? (value === "" ? "" : Number(value)) : value,
    }));
    if (!value) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "Поле не может быть пустым",
      }));
    } else {
      setFormErrors((prev) => {
        const { [name]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSelectChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    if (!value) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: "Выбор обязателен" }));
    } else {
      setFormErrors((prev) => {
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (
      !formValues.name ||
      !formValues.productTypeUuid ||
      !formValues.collectionUuid
    )
      return;
    try {
      if (!uuidItem) {
        // Режим создания: отправляем данные вместе с фото в виде FormData
        const formData = new FormData();
        formData.append("name", formValues.name);
        formData.append("description", formValues.description);
        formData.append("composition", formValues.composition);
        formData.append("price", String(formValues.price));
        formData.append("status", formValues.status);
        formData.append("comment", formValues.comment);
        formData.append("productTypeUuid", formValues.productTypeUuid);
        formData.append("collectionUuid", formValues.collectionUuid);

        // Извлекаем состояние фото (PhotosState) через ref и получаем массив
        const finalPhotosState = photosManagerRef.current?.getPhotosState();
        const finalPhotos: ProductImage[] = finalPhotosState
          ? finalPhotosState.photos
          : [];

        finalPhotos.forEach((photo) => {
          if (photo.file) {
            formData.append("images", photo.file);
          }
        });
        const imagesMetadata = finalPhotos.map((photo) => ({
          altText: photo.altText,
          imageType: photo.imageType,
          position: photo.position,
        }));
        formData.append("imagesMetadata", JSON.stringify(imagesMetadata));

        await createProduct(formData).unwrap();
      } else {
        // Режим редактирования: сначала обновляем основную информацию товара через PATCH (с JSON)
        const productDataJson = {
          name: formValues.name,
          description: formValues.description,
          composition: formValues.composition,
          price: formValues.price,
          status: formValues.status,
          comment: formValues.comment,
          productTypeUuid: formValues.productTypeUuid,
          collectionUuid: formValues.collectionUuid,
        };
        await updateProduct({ uuid: uuidItem, body: productDataJson }).unwrap();

        // Получаем финальное состояние фото из ref
        const finalPhotosState = photosManagerRef.current?.getPhotosState();
        const finalPhotos: ProductImage[] = finalPhotosState
          ? finalPhotosState.photos
          : [];
        const deletedPhotoIds: string[] = finalPhotosState
          ? finalPhotosState.deletedPhotoIds
          : [];

        // Новые фото: те, у которых отсутствует uuid
        const newPhotos = finalPhotos.filter(
          (photo) => !photo.uuid && photo.file,
        );
        const uploadPromises = newPhotos.map((photo) => {
          const fd = new FormData();
          fd.append("file", photo.file as File);
          fd.append("altText", photo.altText);
          fd.append("imageType", photo.imageType);
          fd.append("position", String(photo.position));
          return uploadProductImage({
            productUuid: uuidItem,
            formData: fd,
          }).unwrap();
        });

        // Обновляем существующие фото: для фото с uuid
        const existingPhotos = finalPhotos.filter((photo) => photo.uuid);
        const updatePromises = existingPhotos.map((photo) => {
          const updateData = {
            altText: photo.altText,
            imageType: photo.imageType,
            position: photo.position,
          };
          return updateProductImage({
            productUuid: uuidItem,
            imageUuid: photo.uuid!,
            updateData,
          }).unwrap();
        });

        // Удаляем фото, используя список удалённых фото
        const deletePromises = deletedPhotoIds.map((imageUuid) =>
          deleteProductImage({ productUuid: uuidItem, imageUuid }).unwrap(),
        );

        await Promise.all([
          ...uploadPromises,
          ...updatePromises,
          ...deletePromises,
        ]);
      }
      updatePage();
      closeModal();
    } catch (error) {
      console.error("Ошибка сохранения товара", error);
    }
  };

  return (
    <ModalWrap
      action={closeModal}
      title={`${uuidItem ? "Редактировать" : "Добавить новый"} товар`}
    >
      {isProductLoading ? (
        <LoadBlock />
      ) : (
        <form onSubmit={handleSubmit}>
          <PhotosManager
            key={photosKey}
            ref={photosManagerRef}
            photos={uuidItem && fullProduct ? fullProduct.images || [] : []}
            productUuid={uuidItem}
          />
          <TextInput
            name="name"
            label="Название товара"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.name}
            action={handleInputChange}
            textError={formErrors.name}
          />
          <Select
            items={statusOptions}
            name="status"
            label="Статус товара"
            placeholder="Выберите статус товара"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.status}
            action={(e) => handleSelectChange("status", e.target.value)}
            textError={formErrors.status}
          />
          {isLoadingPTypes ? (
            <Preloader size="small" />
          ) : (
            <Select
              items={productTypes}
              name="productTypeUuid"
              label="Тип товара"
              placeholder="Выберите тип товара"
              width="30rem"
              margin="2rem 0 0 0"
              value={formValues.productTypeUuid}
              action={(e) =>
                handleSelectChange("productTypeUuid", e.target.value)
              }
              textError={formErrors.productTypeUuid}
            />
          )}
          {isLoadingCollections ? (
            <Preloader size="small" />
          ) : (
            <Select
              items={collections}
              name="collectionUuid"
              label="Коллекция"
              placeholder="Выберите коллекцию"
              width="30rem"
              margin="2rem 0 0 0"
              value={formValues.collectionUuid}
              action={(e) =>
                handleSelectChange("collectionUuid", e.target.value)
              }
              textError={formErrors.collectionUuid}
            />
          )}
          <TextArea
            name="description"
            label="Описание товара"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.description}
            action={handleInputChange}
            textError={formErrors.description}
          />
          <TextArea
            name="composition"
            label="Состав"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.composition}
            action={handleInputChange}
            textError={formErrors.composition}
          />
          <NumberInput
            name="price"
            label="Цена"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.price}
            action={handleInputChange}
            textError={formErrors.price}
          />
          <TextArea
            name="comment"
            label="Комментарий"
            width="30rem"
            margin="2rem 0 0 0"
            value={formValues.comment}
            action={handleInputChange}
            textError={formErrors.comment}
          />
          <ButtonWrap>
            <TextButton
              type="submit"
              text="Сохранить"
              color="black"
              margin="2rem 0 0 0"
              //disabled={Object.keys(formErrors).length > 0 || !name}
              preloader={isCreating || isUpdating}
            />
            <TextButton
              type="button"
              text="Отмена"
              color="white"
              margin="2rem 0 0 0"
              action={closeModal}
            />
          </ButtonWrap>
        </form>
      )}
      <TextError text={JSON.stringify(createError || updateError)} />
    </ModalWrap>
  );
};
